import {
  AQBLOCKS_CORNERCLAMP,
  AQBLOCKS_DROPSHADOW_FILL,
  AQBLOCKS_DROPSHADOW_OFFSET,
  AQBLOCKS_LOWLIGHT,
  AQBLOCKS_POINTER_SLOP,
  AQBLOCKS_REFERENCE_MARKER_SIZECLAMP,
  AQBLOCKS_TRANSITION_DURATION,
} from '../consts';
import { Bounds, Vector } from '@ardoq/graph';
import { ArrowType, LineType } from '@ardoq/api-types';
import { BlocksReference, BlocksVisualState } from '../types';
import { currentTimestamp } from '@ardoq/date-time';
import { rectEnclose } from 'tabview/visualizations/misc/geometry';
import { renderReferenceHandles } from './reference.handles';
import { clamp } from 'lodash';
import {
  beginMarkerPlacement,
  createBeginMarkerPath,
  createEndMarkerPath,
  createRoutePath,
  endMarkerPlacement,
} from './reference.paths';
import { RenderContext } from './types';
import { hasVisualState, isReferenceHitVisible } from '../util';
import {
  MarkerPlacement,
  markerShapes,
  paintMarkerShapes,
} from 'tabview/canvasRendering/markerShapes';

/**
 * Replaces a reference's route and seeds a tween animation from the previous
 * route to the new one. If the reference had no prior route the animation has
 * a null `from` (used by the renderer as an "appear" cue rather than an
 * interpolated transition).
 *
 * Side effects: mutates `reference.animation`, `reference.route`, and
 * `reference.bounds`. The bounds are recomputed as the AABB of the new route.
 */
export const setReferenceRoute = (
  reference: BlocksReference,
  route: Vector[]
) => {
  reference.animation = {
    begin: currentTimestamp(),
    duration: AQBLOCKS_TRANSITION_DURATION,
    from: reference.route ? reference.route : null,
  };

  reference.route = route;
  reference.bounds = rectEnclose(...route);
};

/**
 * Tests whether a world-space point lies within `slop` of the reference's
 * routed polyline.
 *
 * Three-stage check, cheapest first: hit-visibility predicate → AABB
 * rejection against `reference.bounds` → per-segment distance test. The
 * per-segment test fast-paths Manhattan (axis-aligned) segments — common in
 * routed diagrams — and falls back to the full point-to-segment distance for
 * arbitrary segments.
 *
 * @param pos  world-space coordinates of the test point.
 * @param slop  tolerance in world units (callers typically pass a
 *   device-pixel slop divided by the projection scale).
 * @returns true if the point hits the reference within `slop`.
 */
export const hitTestReference = (
  reference: BlocksReference,
  pos: Vector,
  resolution: number
) => {
  if (
    !reference.bounds ||
    !reference.route ||
    !isReferenceHitVisible(reference)
  ) {
    return false;
  }

  const slop = AQBLOCKS_POINTER_SLOP / resolution;

  if (
    pos[0] < reference.bounds[0] - slop ||
    pos[1] < reference.bounds[1] - slop ||
    pos[0] > reference.bounds[2] + slop ||
    pos[1] > reference.bounds[3] + slop
  ) {
    return false;
  }

  const hitTestSegment = (a: Vector, b: Vector) => {
    /* trivial exclusion */
    if (pos[0] < Math.min(a[0], b[0]) - slop) return false;
    if (pos[0] > Math.max(a[0], b[0]) + slop) return false;
    if (pos[1] < Math.min(a[1], b[1]) - slop) return false;
    if (pos[1] > Math.max(a[1], b[1]) + slop) return false;

    if (Math.abs(a[0] - b[0]) < 1 || Math.abs(a[1] - b[1]) < 1) {
      /* not trivially excluded from a manhattan segment is an inclusion */
      return true;
    }

    /* pull out the big guns for non manhattan segment */
    const sqr = (a: number) => a * a;
    const dist2 = (v: Vector, w: Vector) => sqr(v[0] - w[0]) + sqr(v[1] - w[1]);

    const l2 = dist2(a, b);
    if (l2 === 0) {
      return false; // distance to end of zero length segment
    }

    const t = clamp(
      ((pos[0] - a[0]) * (b[0] - a[0]) + (pos[1] - a[1]) * (b[1] - a[1])) / l2,
      0,
      1
    );
    const d2 = dist2(pos, [a[0] + t * (b[0] - a[0]), a[1] + t * (b[1] - a[1])]);

    return d2 < sqr(slop);
  };

  let a = reference.route[0];

  for (let i = 1; i < reference.route.length; ++i) {
    const b = reference.route[i];

    if (hitTestSegment(a, b)) {
      return true;
    }

    a = b;
  }

  return false;
};
/**
 * Renders a single reference (the line + its two end markers + optional
 * edit handles) onto the canvas.
 *
 * The composition is layered, drawn in this order so each layer correctly
 * appears over the previous:
 *   1. **Marker hover dropshadow** — only when hovered and the markers are
 *      large enough; provides a "halo" behind each end marker.
 *   2. **Line hover dropshadow** — broad stroke under the line.
 *   3. **The line itself** — using the reference's colour (darkened slightly
 *      on hover) and dash array.
 *   4. **End markers** — beginning and ending arrowheads. Tiny-marker case
 *      degrades to a 1-CSS-pixel `fillRect` `STANDIN` for visibility.
 *   5. **Edit handles** — only when the reference is selected *and* the
 *      view is editable. Delegated to `renderReferenceHandles`.
 *
 * Culling: returns early without drawing anything when the reference is in the `Hidden`
 * visual state, or (b) the route has fewer than two waypoints.
 *
 * @param globalAlpha  per-frame animation alpha in `[0, 1]`, multiplied with the
 *   lowlight global alpha and applied to `ctx.globalAlpha`.
 */
export const renderReference = (
  reference: BlocksReference,
  bounds: Bounds,
  route: Vector[],
  globalAlpha: number,
  params: RenderContext
): boolean => {
  const isHidden = hasVisualState(reference, BlocksVisualState.Hidden);

  if (isHidden || route.length < 2) {
    return true;
  }

  const isHighlight = hasVisualState(reference, BlocksVisualState.Highlight);
  const isLowlight = params.isLowlight && !isHighlight;
  const isHover = hasVisualState(reference, BlocksVisualState.Hover);
  const isSelect = hasVisualState(reference, BlocksVisualState.Select);
  const isEditable =
    !params.isReadonly && true; /* reference is individually editable */

  const color = isHover
    ? params.referenceHoverStroke(reference.color)
    : reference.color;
  const routeWidth =
    isSelect && !isEditable
      ? params.selectedReferenceSize
      : params.referenceSize;
  const shadowWidth = routeWidth + 2 * AQBLOCKS_DROPSHADOW_OFFSET;

  const { ctx, projection } = params;

  params.ctx.globalAlpha = globalAlpha * (isLowlight ? AQBLOCKS_LOWLIGHT : 1);

  /* marker hover */

  if (
    isHover &&
    params.referenceMarkerSize > AQBLOCKS_REFERENCE_MARKER_SIZECLAMP[0]
  ) {
    if (
      reference.lineBeginning !== ArrowType.NONE &&
      params.referenceMarkerSize > 1
    ) {
      ctx.beginPath();
      createBeginMarkerPath(reference, route, params);
      ctx.lineWidth = shadowWidth;
      ctx.strokeStyle = AQBLOCKS_DROPSHADOW_FILL;
      ctx.stroke();
    }

    if (
      reference.lineEnding !== ArrowType.NONE &&
      params.referenceMarkerSize > 1
    ) {
      ctx.beginPath();
      createEndMarkerPath(reference, route, params);
      ctx.lineWidth = shadowWidth;
      ctx.strokeStyle = AQBLOCKS_DROPSHADOW_FILL;
      ctx.stroke();
    }
  }

  /* create route path */

  params.ctx.beginPath();
  createRoutePath(reference, bounds, route, params);

  if (isHover) {
    /* route hover */
    ctx.lineWidth = shadowWidth;
    ctx.strokeStyle = AQBLOCKS_DROPSHADOW_FILL;
    ctx.stroke();
  }

  /* route */

  ctx.lineWidth = routeWidth;
  ctx.strokeStyle = color;

  if (isSelect && reference.lineType !== LineType.SOLID) {
    const globalAlpha = ctx.globalAlpha;
    ctx.globalAlpha = ctx.globalAlpha * 0.15;
    ctx.stroke();
    ctx.globalAlpha = globalAlpha;
  }

  if (reference.lineType !== LineType.SOLID) {
    ctx.setLineDash(params.dashArray(reference.lineType));
  }

  ctx.stroke();

  if (reference.lineType !== LineType.SOLID) {
    ctx.setLineDash([]);
  }

  if (params.referenceMarkerSize > AQBLOCKS_REFERENCE_MARKER_SIZECLAMP[0]) {
    /* markers */
    ctx.lineWidth = routeWidth;

    if (reference.lineBeginning !== ArrowType.NONE) {
      if (params.referenceMarkerSize < 1) {
        /* STANDIN */
        const pt = projection.projectPointSnapped(route[0]);

        ctx.fillStyle = color;
        ctx.fillRect(pt[0] - 0.5, pt[1] + 0.5, 1, 1);
      } else {
        const arrowType: ArrowType = reference.lineBeginning;
        const placement: MarkerPlacement | null = beginMarkerPlacement(
          reference,
          route,
          params
        );

        if (placement) {
          paintMarkerShapes(
            params.ctx,
            markerShapes(arrowType),
            placement,
            color,
            params.referenceMarkerFill(color)
          );
        }
      }
    }

    if (reference.lineEnding !== ArrowType.NONE) {
      if (params.referenceMarkerSize < 1) {
        const pt = projection.projectPointSnapped(route[route.length - 1]);

        ctx.fillStyle = color;
        ctx.fillRect(pt[0] - 0.5, pt[1] + 0.5, 1, 1);
      } else {
        const arrowType: ArrowType = reference.lineEnding;
        const placement: MarkerPlacement | null = endMarkerPlacement(
          reference,
          route,
          params
        );

        if (placement) {
          paintMarkerShapes(
            params.ctx,
            markerShapes(arrowType),
            placement,
            color,
            params.referenceMarkerFill(color)
          );
        }
      }
    }
  }

  if (isSelect && isEditable) {
    renderReferenceHandles(reference, bounds, route, params);
  }

  return true;
};

export const renderReferenceLabel = (
  reference: BlocksReference,
  _bounds: Bounds,
  route: Vector[],
  _alpha: number,
  params: RenderContext
) => {
  const { labelSegment } = reference;
  const label = 'yo mamma'; // reference.labels.length > 0? reference.labels[0]: undefined;

  if (!reference.globalTypeId || labelSegment === undefined || !label) {
    return;
  }

  const fillStyle = params.referenceMarkerFill(reference.color);
  const textStyle = params.labelColor(fillStyle);

  const p0 = route[labelSegment];
  const p1 = route[labelSegment + 1];
  const c = params.projection.projectPointSnapped([
    0.5 * (p0[0] + p1[0]),
    0.5 * (p0[1] + p1[1]),
  ]);

  params.ctx.font = params.labelFont;
  const textMetrics = params.ctx.measureText(label);
  const height = Math.ceil(
    textMetrics.fontBoundingBoxAscent + textMetrics.fontBoundingBoxDescent
  );
  const cornerRadius = height / 2;
  const width = Math.ceil(textMetrics.width + 2 * cornerRadius);

  params.ctx.beginPath();
  if (cornerRadius > AQBLOCKS_CORNERCLAMP[0]) {
    params.ctx.roundRect(
      c[0] - 0.5 * width,
      c[1] - 0.5 * height,
      width,
      height,
      cornerRadius
    );
  } else {
    params.ctx.rect(c[0] - 0.5 * width, c[1] - 0.5 * height, width, height);
  }
  params.ctx.fillStyle = fillStyle;
  params.ctx.fill();

  params.ctx.strokeStyle = reference.color;
  params.ctx.lineWidth = params.borderSize;
  params.ctx.stroke();

  params.ctx.fillStyle = textStyle;
  params.ctx.textAlign = 'center';
  params.ctx.fillText(label, ...c);
};
